import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { Pro013CampaignData } from "../create-pro013-form";

interface StepFourProductsProps {
  data: Pro013CampaignData;
  updateData: (data: Partial<Pro013CampaignData>) => void;
  nextStep: () => void;
  prevStep: () => void;
}

export default function StepFourProducts({ data, updateData, nextStep, prevStep }: StepFourProductsProps) {
  const [selectionType, setSelectionType] = useState<"departments" | "groups">(
    data.groups.length > 0 ? "groups" : "departments"
  );
  const [deptInput, setDeptInput] = useState("");
  const [groupInput, setGroupInput] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isFormValid = useMemo(() => {
    const hasSelection = data.departments.length > 0 || data.groups.length > 0;
    const groupsValid = data.groups.length === 0 || data.departments.length <= 1;
    return hasSelection && groupsValid;
  }, [data.departments, data.groups]);

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Ao menos 1 departamento ou grupo
    if (data.departments.length === 0 && data.groups.length === 0) {
      newErrors.selection = "Selecione pelo menos 1 departamento ou grupo";
    }

    // Grupos apenas com 1 departamento
    if (data.groups.length > 0 && data.departments.length > 1) {
      newErrors.groups = "Ao usar grupos, é permitido apenas 1 departamento";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      nextStep();
    }
  };

  const addDepartment = () => {
    const deptCode = parseInt(deptInput);
    if (!isNaN(deptCode) && deptCode > 0 && deptCode <= 99) {
      if (!data.departments.includes(deptCode)) {
        updateData({ departments: [...data.departments, deptCode] });
        setDeptInput("");
        setErrors({});
      } else {
        setErrors({ departments: "Departamento já adicionado" });
      }
    } else {
      setErrors({ departments: "Código inválido (deve ser entre 1 e 99)" });
    }
  };

  const removeDepartment = (code: number) => {
    updateData({ departments: data.departments.filter(d => d !== code) });
  };

  const addGroup = () => {
    // Validar que existe exatamente 1 departamento antes de adicionar grupo
    if (data.departments.length === 0) {
      setErrors({ groups: "Adicione um departamento antes de adicionar grupos" });
      return;
    }
    if (data.departments.length > 1) {
      setErrors({ groups: "Remova departamentos extras. Grupos só podem ser usados com 1 departamento" });
      return;
    }

    const groupCode = parseInt(groupInput);
    if (!isNaN(groupCode) && groupCode > 0 && groupCode <= 99) {
      if (!data.groups.includes(groupCode)) {
        updateData({ groups: [...data.groups, groupCode] });
        setGroupInput("");
        setErrors({});
      } else {
        setErrors({ groups: "Grupo já adicionado" });
      }
    } else {
      setErrors({ groups: "Código inválido (deve ser entre 1 e 99)" });
    }
  };

  const removeGroup = (code: number) => {
    updateData({ groups: data.groups.filter(g => g !== code) });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Passo 4: Departamentos ou Grupos</CardTitle>
        <CardDescription>
          Defina quais departamentos ou grupos de produtos participam da promoção
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Tipo de Seleção */}
        <div className="space-y-4">
          <Label>
            Tipo de Seleção <span className="text-red-500">*</span>
          </Label>
          <RadioGroup
            value={selectionType}
            onValueChange={(value) => {
              setSelectionType(value as "departments" | "groups");
              // Limpar erros
              setErrors({});
            }}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="departments" id="departments" />
              <Label htmlFor="departments" className="font-normal cursor-pointer">
                Departamentos
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="groups" id="groups" />
              <Label htmlFor="groups" className="font-normal cursor-pointer">
                Grupos
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Seleção de Departamentos */}
        {selectionType === "departments" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="deptInput">
                Adicionar Departamento <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="deptInput"
                  type="number"
                  min="1"
                  max="99"
                  placeholder="Código do departamento (ex: 30)"
                  value={deptInput}
                  onChange={(e) => setDeptInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addDepartment()}
                  className={errors.departments ? "border-red-500" : ""}
                />
                <Button type="button" onClick={addDepartment} disabled={!deptInput}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {errors.departments && (
                <p className="text-sm text-red-500">{errors.departments}</p>
              )}
            </div>

            {/* Lista de Departamentos */}
            {data.departments.length > 0 && (
              <div className="space-y-2">
                <Label>Departamentos Selecionados ({data.departments.length})</Label>
                <div className="flex flex-wrap gap-2 p-4 border rounded-md">
                  {data.departments.map((code) => (
                    <Badge key={code} variant="secondary" className="text-sm">
                      Departamento {code}
                      <button
                        onClick={() => removeDepartment(code)}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Seleção de Grupos */}
        {selectionType === "groups" && (
          <div className="space-y-4">
            {/* Alerta se não tiver departamento */}
            {data.departments.length === 0 && (
              <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Primeiro adicione um departamento.</strong> Grupos só podem ser selecionados quando há exatamente 1 departamento escolhido.
                </p>
              </div>
            )}

            {/* Alerta se tiver mais de 1 departamento */}
            {data.departments.length > 1 && (
              <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  Ao usar grupos, apenas 1 departamento é permitido.
                  Remova departamentos extras ou volte para o modo "Departamentos".
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="groupInput">
                Adicionar Grupo <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="groupInput"
                  type="number"
                  min="1"
                  max="99"
                  placeholder={data.departments.length !== 1 ? "Adicione um departamento primeiro" : "Código do grupo (ex: 10)"}
                  value={groupInput}
                  onChange={(e) => setGroupInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addGroup()}
                  className={errors.groups ? "border-red-500" : ""}
                  disabled={data.departments.length !== 1}
                />
                <Button
                  type="button"
                  onClick={addGroup}
                  disabled={!groupInput || data.departments.length !== 1}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {errors.groups && (
                <p className="text-sm text-red-500">{errors.groups}</p>
              )}
            </div>

            {/* Lista de Grupos */}
            {data.groups.length > 0 && (
              <div className="space-y-2">
                <Label>Grupos Selecionados ({data.groups.length})</Label>
                <div className="flex flex-wrap gap-2 p-4 border rounded-md">
                  {data.groups.map((code) => (
                    <Badge key={code} variant="secondary" className="text-sm">
                      Grupo {code}
                      <button
                        onClick={() => removeGroup(code)}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Erro geral de seleção */}
        {errors.selection && (
          <div className="bg-red-50 p-3 rounded-md border border-red-200">
            <p className="text-sm text-red-800">{errors.selection}</p>
          </div>
        )}

        {/* Botões */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={prevStep}>
            <ChevronLeft className="mr-2 h-4 w-4" /> Voltar
          </Button>
          <Button onClick={handleNext} disabled={!isFormValid}>
            Próximo: Revisão
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
